import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge';
import { useWorkerStatus, WorkerStatus } from '../hooks/useWorkerStatus';
import { MATURITY_LEVELS } from '../workerConfig';
import { useFlags } from '../App';

type StaticStatus = 'live' | 'degraded' | 'building' | 'offline' | 'checking';

const MATURITY_APPS: Array<{
  level: number;
  name: string;
  tagline: string;
  products: string[];
  url: string;
  workerKey: 'l1' | 'l2' | 'l3' | 'l4' | 'l5';
  color: string;
}> = [
  {
    level: 1,
    name: 'shrtn.dev',
    tagline: 'URL Shortener',
    products: ['Workers'],
    url: MATURITY_LEVELS.l1,
    workerKey: 'l1',
    color: '#f97316',
  },
  {
    level: 2,
    name: 'FlagForge',
    tagline: 'Feature Flag Dashboard',
    products: ['Workers', 'KV', 'R2'],
    url: MATURITY_LEVELS.l2,
    workerKey: 'l2',
    color: '#eab308',
  },
  {
    level: 3,
    name: 'EdgeChat',
    tagline: 'Real-time Chat',
    products: ['Workers', 'Durable Objects', 'D1', 'KV'],
    url: MATURITY_LEVELS.l3,
    workerKey: 'l3',
    color: '#22c55e',
  },
  {
    level: 4,
    name: 'PixelEdge',
    tagline: 'Image Upload Platform',
    products: ['Workers', 'DO', 'D1', 'R2', 'Queues'],
    url: MATURITY_LEVELS.l4,
    workerKey: 'l4',
    color: '#3b82f6',
  },
  {
    level: 5,
    name: 'SupportAI',
    tagline: 'AI Support Assistant',
    products: ['Workers AI', 'D1', 'R2'],
    url: MATURITY_LEVELS.l5,
    workerKey: 'l5',
    color: '#a855f7',
  },
];

const DOMAINS: Array<{
  path: string;
  icon: string;
  iconBg: string;
  accent: string;
  title: string;
  url: string;
  description: string;
  demos: string[];
  staticStatus: StaticStatus;
  workerKey?: 'edgeApi' | 'kvDemo' | 'd1Demo' | 'r2Demo' | 'aiDemo' | 'securityDemo';
}> = [
  {
    path: '/security',
    icon: '🛡️',
    iconBg: '#fff0f0',
    accent: '#ef4444',
    title: 'Security Demos',
    url: 'shrutigames.com',
    description:
      'Live security in action: Bot Management, Turnstile CAPTCHA, WAF rule inspection, and rate limiting with real-time counters.',
    demos: ['Bot Management', 'Turnstile', 'WAF Rules', 'Rate Limiting'],
    staticStatus: 'checking',
    workerKey: 'securityDemo',
  },
  {
    path: '/performance',
    icon: '⚡',
    iconBg: '#fffbeb',
    accent: '#f59e0b',
    title: 'CDN & Performance',
    url: 'shrutidances.com',
    description:
      "Explore planned CDN demonstrations for cache behavior, image optimization, and geographic routing.",
    demos: ['Cache Explorer', 'Image Optimization', 'Geo Routing', 'Cache Rules'],
    staticStatus: 'building',
  },
  {
    path: '/devplatform',
    icon: '⚙️',
    iconBg: '#f0f4ff',
    accent: '#0051c3',
    title: 'Developer Platform',
    url: 'shruti-techlab.com',
    description:
      'Interactive Workers, D1 SQL, KV, and R2 demonstrations running on Cloudflare.',
    demos: ['Workers', 'D1', 'KV', 'R2'],
    staticStatus: 'checking',
    workerKey: 'edgeApi',
  },
  {
    path: '/ai',
    icon: '🤖',
    iconBg: '#f3f0ff',
    accent: '#7c3aed',
    title: 'AI Platform',
    url: 'shruti-ai.dev',
    description:
      'Workers AI inference with application-level caching, usage metrics, search prototypes, and sentiment analysis.',
    demos: ['LLM Chat', 'App Cache', 'Search Prototype', 'Sentiment'],
    staticStatus: 'checking',
    workerKey: 'aiDemo',
  },
];

const SEARCH_DATA = [
    { title: 'Security Demos', desc: 'Bot Management, Turnstile, WAF, Rate Limiting', path: '/security', keywords: 'security bot waf firewall turnstile captcha rate limit ddos' },
    { title: 'CDN & Performance', desc: 'Cache, Image Optimization, Geo Routing', path: '/performance', keywords: 'cdn cache performance speed image optimize geo' },
    { title: 'Developer Platform', desc: 'Workers, D1, KV, R2, Queues, Durable Objects', path: '/devplatform', keywords: 'workers d1 kv r2 queues durable objects developer serverless' },
    { title: 'AI Platform', desc: 'Workers AI, AI Gateway, RAG, Vectorize', path: '/ai', keywords: 'ai llm workers ai gateway rag vector embeddings' },
    { title: 'FlagForge (L2)', desc: 'Feature Flags with KV & R2', path: MATURITY_LEVELS.l2, keywords: 'feature flags kv r2 toggle rollout' },
    { title: 'EdgeChat (L3)', desc: 'Real-time Chat with Durable Objects', path: MATURITY_LEVELS.l3, keywords: 'chat realtime websocket durable objects' },
    { title: 'PixelEdge (L4)', desc: 'Image Upload with Queues', path: MATURITY_LEVELS.l4, keywords: 'image upload queue r2 storage' },
    { title: 'SupportAI (L5)', desc: 'AI Support Assistant', path: MATURITY_LEVELS.l5, keywords: 'ai support assistant llm' },
];

// Site search component - only shown when ai-search flag is enabled
function AISearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<{ title: string; desc: string; path: string }>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    const q = query.toLowerCase();
    const matches = SEARCH_DATA.filter(
      d => d.title.toLowerCase().includes(q) || d.keywords.includes(q) || d.desc.toLowerCase().includes(q)
    ).slice(0, 5);
    setResults(matches);
    setIsOpen(matches.length > 0);
  }, [query]);

  const handleSelect = (path: string) => {
    setIsOpen(false);
    setQuery('');
    if (path.startsWith('http')) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  return (
    <div className="ai-search-container">
      <span className="ai-search-icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="text"
        className="ai-search-input"
        placeholder="Search demos with AI..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
      />
      <span className="ai-search-badge">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
        AI
      </span>
      {isOpen && (
        <div className="ai-search-results">
          {results.map((r, i) => (
            <div key={i} className="ai-search-result" onClick={() => handleSelect(r.path)}>
              <div className="ai-search-result-title">{r.title}</div>
              <div className="ai-search-result-desc">{r.desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const workerStatuses = useWorkerStatus();
  const flags = useFlags();

  const getStatus = (d: typeof DOMAINS[0]): StaticStatus => {
    if (d.path === '/devplatform') {
      const dependencies = [workerStatuses.edgeApi, workerStatuses.kvDemo, workerStatuses.d1Demo, workerStatuses.r2Demo];
      if (dependencies.includes('checking')) return 'checking';
      if (dependencies.every((status) => status === 'live')) return 'live';
      return dependencies.includes('offline') ? 'offline' : 'degraded';
    }
    if (d.workerKey) return workerStatuses[d.workerKey] as WorkerStatus;
    return d.staticStatus;
  };

  const liveCount = DOMAINS.filter((d) => getStatus(d) === 'live').length;

  return (
    <>
      <section className="hero">
        <div className="hero-eyebrow">
          <span>●</span> Live Environment
        </div>
        <h1>
          Shruti's <span>Cloudflare</span> Lab
        </h1>
        <p className="hero-sub">
          Interactive demos across security, performance, and the developer platform.
        </p>
        <div className="hero-stats">
          <div className="hero-stat">
            <span className="hero-stat-num">{DOMAINS.length}</span>
            <div className="hero-stat-label">Demo Areas</div>
          </div>
          <div className="hero-stat">
            <span className="hero-stat-num">9</span>
            <div className="hero-stat-label">Apps Built</div>
          </div>
        </div>
      </section>

      <main className="main">
        {/* AI Search - controlled by FlagForge ai-search flag */}
        {flags.aiSearch && <AISearch />}

        <div className="section-header">
          <div className="section-title">
            <span className="dot" /> Demo Areas
          </div>
          <span style={{ fontSize: '0.8rem', color: 'var(--cf-muted)' }}>
            {liveCount} of {DOMAINS.length} live
          </span>
        </div>

        <div className="domain-grid">
          {DOMAINS.map((d) => (
            <div
              key={d.path}
              className="domain-card"
              style={{ '--card-accent': d.accent } as React.CSSProperties}
              onClick={() => navigate(d.path)}
            >
              <div className="domain-card-header">
                <div
                  className="domain-icon"
                  style={{ background: d.iconBg }}
                >
                  {d.icon}
                </div>
                <StatusBadge status={getStatus(d)} />
              </div>

              <div>
                <h3>{d.title}</h3>
                <div className="domain-card-url">{d.url}</div>
              </div>

              <p>{d.description}</p>

              <div className="domain-card-demos">
                {d.demos.map((tag) => (
                  <span key={tag} className="demo-tag">{tag}</span>
                ))}
              </div>

              <div className="domain-card-cta">
                Explore demos
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          ))}
        </div>

        <div className="section-header" style={{ marginTop: '2rem' }}>
          <div className="section-title">
            <span className="dot" /> Developer Maturity Levels
          </div>
          <span style={{ fontSize: '0.8rem', color: 'var(--cf-muted)' }}>
            shruti-techlab.com — progressive Cloudflare product adoption
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
          {MATURITY_APPS.map((app) => (
            <a
              key={app.level}
              href={app.url}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: 'none',
                border: '1px solid var(--cf-border)',
                borderRadius: '0.75rem',
                padding: '1rem',
                background: 'var(--cf-surface)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 0 0 2px ${app.color}40`)}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  background: app.color,
                  color: '#fff',
                  borderRadius: '0.25rem',
                  padding: '0.15rem 0.4rem',
                }}>
                  L{app.level}
                </span>
                <StatusBadge status={workerStatuses[app.workerKey]} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--cf-text)' }}>{app.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--cf-muted)' }}>{app.tagline}</div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                {app.products.map((p) => (
                  <span key={p} style={{
                    fontSize: '0.65rem',
                    padding: '0.1rem 0.35rem',
                    borderRadius: '0.25rem',
                    background: `${app.color}18`,
                    color: app.color,
                    fontWeight: 500,
                  }}>{p}</span>
                ))}
              </div>
            </a>
          ))}
        </div>

        <div className="section-header" style={{ marginTop: '1.5rem' }}>
          <div className="section-title">
            <span className="dot" /> Quick Access
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <a
            href="https://dash.cloudflare.com"
            target="_blank"
            rel="noreferrer"
            className="btn btn-secondary"
          >
            Cloudflare Dashboard ↗
          </a>
          <a
            href="https://turnstile.shruti-test.com/good"
            target="_blank"
            rel="noreferrer"
            className="btn btn-secondary"
          >
            Turnstile Live Demo ↗
          </a>
          <a
            href="https://shrutidances.com"
            target="_blank"
            rel="noreferrer"
            className="btn btn-secondary"
          >
            CDN Demo ↗
          </a>
        </div>
      </main>
    </>
  );
}
